import React, { useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { KTSVG, SimpleAlert } from '../../../../../rbts/helpers';
import { styleSelect, themeSelect } from '../../../../../rbts/assets/ts/_utils';
import { guardarProductoService } from '../../../../service/catalogos/Productos';
import { useObtenerProductoPorId } from '../hooks/ProductosHook';
import { TIPOS_PRODUCTO, UNIDADES_PRODUCTO } from '../models/ProductoModel';

type Props = {
  show: boolean;
  handleClose: (updateGrid: boolean) => void;
  id_row: number;
};

const schema = Yup.object().shape({
  nombre: Yup.string().trim().required('El nombre es requerido.').max(800, 'Máximo 800 caracteres.'),
  tipo: Yup.string().required('El tipo es requerido.'),
  unidad: Yup.string().trim().required('La unidad es requerida.').max(20, 'Máximo 20 caracteres.'),
  costo_sugerido: Yup.number()
    .typeError('Debe ser un número.')
    .required('El costo sugerido es requerido.')
    .min(0, 'No puede ser negativo.'),
  util_proveedor: Yup.number().typeError('Debe ser un número.').nullable().min(0, 'No puede ser negativo.'),
});

const CreateModal: React.FC<Props> = ({ id_row, show, handleClose }) => {
  const [loading, setLoading] = useState(false);
  const { initValues } = useObtenerProductoPorId(id_row);

  const formik = useFormik({
    initialValues: initValues,
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        await guardarProductoService(values);
        SimpleAlert(
          id_row ? 'Producto actualizado exitosamente.' : 'Producto creado exitosamente.',
          3000,
          'success'
        );
        handleClose(true);
        formik.resetForm();
      } catch (error: any) {
        const message = error?.response?.data?.message ?? 'Ocurrió un error.';
        SimpleAlert(message, 3000, 'error');
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  return (
    <Modal
      id="kt_modal_producto"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog modal-dialog-centered mw-550px"
      show={show}
      onHide={() => {
        formik.resetForm();
        handleClose(false);
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2>{id_row ? 'Editar producto' : 'Nuevo producto'}</h2>
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            onClick={() => {
              formik.resetForm();
              handleClose(false);
            }}
          >
            <KTSVG className="svg-icon-2" path="/media/icons/duotune/arrows/arr061.svg" />
          </div>
        </div>
        <div className="modal-body mx-5">
          <form onSubmit={formik.handleSubmit}>

            {/* Nombre */}
            <div className="fv-row mb-7">
              <div className="w-100 d-flex">
                <label className="required fw-bold fs-6 mb-2">Nombre</label>
                {formik.touched.nombre && formik.errors.nombre && (
                  <div className="fv-plugins-message-container text-danger ms-5">
                    <span role="alert">{formik.errors.nombre}</span>
                  </div>
                )}
              </div>
              <input
                placeholder="Nombre del producto"
                {...formik.getFieldProps('nombre')}
                className="form-control"
                type="text"
                autoComplete="off"
              />
            </div>

            {/* Tipo */}
            <div className="fv-row mb-7">
              <div className="w-100 d-flex">
                <label className="required fw-bold fs-6 mb-2">Tipo</label>
                {formik.touched.tipo && formik.errors.tipo && (
                  <div className="fv-plugins-message-container text-danger ms-5">
                    <span role="alert">{formik.errors.tipo}</span>
                  </div>
                )}
              </div>
              <Select
                options={TIPOS_PRODUCTO}
                value={TIPOS_PRODUCTO.find((t) => t.value === formik.values.tipo) ?? null}
                onChange={(opt) => formik.setFieldValue('tipo', opt?.value ?? '')}
                styles={styleSelect}
                theme={themeSelect}
                classNamePrefix="tc-select"
                placeholder="Seleccione un tipo"
                isSearchable={false}
              />
            </div>

            {/* Unidad */}
            <div className="fv-row mb-7">
              <div className="w-100 d-flex">
                <label className="required fw-bold fs-6 mb-2">Unidad</label>
                {formik.touched.unidad && formik.errors.unidad && (
                  <div className="fv-plugins-message-container text-danger ms-5">
                    <span role="alert">{formik.errors.unidad}</span>
                  </div>
                )}
              </div>
              <Select
                options={UNIDADES_PRODUCTO}
                value={UNIDADES_PRODUCTO.find((u) => u.value === formik.values.unidad) ?? null}
                onChange={(opt) => formik.setFieldValue('unidad', opt?.value ?? '')}
                styles={styleSelect}
                theme={themeSelect}
                classNamePrefix="tc-select"
                placeholder="Seleccione una unidad"
                isSearchable={false}
              />
            </div>

            {/* Costo sugerido */}
            <div className="fv-row mb-7">
              <div className="w-100 d-flex">
                <label className="required fw-bold fs-6 mb-2">Costo sugerido</label>
                {formik.touched.costo_sugerido && formik.errors.costo_sugerido && (
                  <div className="fv-plugins-message-container text-danger ms-5">
                    <span role="alert">{formik.errors.costo_sugerido as string}</span>
                  </div>
                )}
              </div>
              <input
                placeholder="0.00"
                {...formik.getFieldProps('costo_sugerido')}
                className="form-control"
                type="number"
                step="0.01"
                min="0"
                autoComplete="off"
              />
            </div>

            {/* Utilidad proveedor */}
            <div className="fv-row mb-7">
              <div className="w-100 d-flex">
                <label className="fw-bold fs-6 mb-2">Utilidad proveedor (Opcional)</label>
                {formik.touched.util_proveedor && formik.errors.util_proveedor && (
                  <div className="fv-plugins-message-container text-danger ms-5">
                    <span role="alert">{formik.errors.util_proveedor as string}</span>
                  </div>
                )}
              </div>
              <input
                placeholder="0.00"
                {...formik.getFieldProps('util_proveedor')}
                className="form-control"
                type="number"
                step="0.01"
                min="0"
                autoComplete="off"
              />
            </div>

            <div className="text-center pt-10">
              <button
                type="reset"
                className="btn btn-light me-3"
                onClick={() => {
                  formik.resetForm();
                  handleClose(false);
                }}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {!loading ? (
                  <span>{id_row ? 'Actualizar' : 'Guardar'}</span>
                ) : (
                  <span style={{ display: 'block' }}>
                    Espere por favor...
                    <span className="spinner-border spinner-border-sm align-middle ms-2" />
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export { CreateModal };
